import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { reject } from "lodash";
import { Cookies } from "../constants/Cookies";
import { businessApiKey } from "../models/apiKey";
import { BusinessApiResponseModel } from "../models/apiModel/apiResponseModel";
import { ImpCallbackResponseModel } from "../models/payment/iamport/ImpModels";
import { UserInfoModel } from "../models/user/UserInfo";
import GlobalEnvironments from "../modules/globalEnvironment/GlobalEnvironment";
import { Project } from "./models";
import { util } from "./util";
import { Post, Comment } from "../models/communityModel";
import { AIAnswer, AIComment, AIPost } from "../models/aiCommunityModel";

export const api = {
  // 타사 적산 파일(나스 .SGS / 다산 .est·.estx / 글로텍 .EBS / 이지테크 .kdbx / 하우코스트 .xct / 아성 .asgs)을 서버에서 ConAX 프로젝트 문자열(.conax 형식)로 변환
  // 타사 적산 파일 → .conax 텍스트. 서버가 되물으면(수량 없는 자료집 / 46MB 초과) 400 + JSON 이 오는데,
  // 그때는 Error 에 refused(reason·message·uploadToken…)를 붙여 던진다 — 화면이 '단가집으로 올리기'·'그래도 열기'를 제안한다.
  // source 가 {uploadToken} 이면 조금 전 올린 파일(서버 20분 보관)을 다시 올리지 않고 이어서 쓴다.
  convertEstimationToConax: async (
    source: File | { uploadToken: string },
    opts: { force?: boolean; onUploadProgress?: (percent: number) => void } = {}
  ): Promise<string> => {
    const key = util.getCookie(Cookies.authKey);
    const form = new FormData();
    if (source instanceof File) form.append("file", source);
    else form.append("upload_token", source.uploadToken);
    const config: any = { headers: { Authorization: `Token ${key}` }, responseType: "text" };
    if (opts.onUploadProgress && source instanceof File) {
      config.onUploadProgress = (e: any) => {
        if (e && e.total) opts.onUploadProgress!(Math.floor((e.loaded / e.total) * 100));
      };
    }
    try {
      const res = await axios.post(
        `${GlobalEnvironments.getApiUrl()}project/import/estimation-file?target=conax${opts.force ? "&force=1" : ""}`,
        form,
        config
      );
      return res.data as string;
    } catch (error: any) {
      let body: any = null;
      try {
        body = typeof error?.response?.data === "string" ? JSON.parse(error.response.data) : error?.response?.data;
      } catch (e) {}
      if (body && body.reason) {
        throw Object.assign(new Error(body.message), {
          refused: {
            reason: body.reason as "no_quantity" | "too_large" | "token_expired",
            message: body.message as string,
            hasQuantity: !!body.has_quantity,
            counts: body.counts || {},
            limitMb: body.limit_mb as number | undefined,
            uploadToken: (body.upload_token as string) || "",
            fileName: (body.file_name as string) || "",
          },
        });
      }
      throw error;
    }
  },
  // 파일(또는 방금 올린 파일의 토큰) → 단가집. preview 면 담길 개수·내 단가집 목록, save 면 새 단가집 또는 기존 단가집의 새 판.
  importToMyLibrary: async (args: {
    file?: File;
    uploadToken?: string;
    mode: "preview" | "save";
    name?: string;
    libraryId?: string;
    onUploadProgress?: (percent: number) => void;
  }): Promise<any> => {
    const key = util.getCookie(Cookies.authKey);
    const form = new FormData();
    if (args.file) form.append("file", args.file, args.file.name);
    else if (args.uploadToken) form.append("upload_token", args.uploadToken);
    form.append("mode", args.mode);
    if (args.name) form.append("name", args.name);
    if (args.libraryId) form.append("library_id", args.libraryId);
    const config: any = { headers: { Authorization: `Token ${key}` } };
    if (args.onUploadProgress && args.file) {
      config.onUploadProgress = (e: any) => {
        if (e && e.total) args.onUploadProgress!(Math.floor((e.loaded / e.total) * 100));
      };
    }
    try {
      const res = await axios.post(`${GlobalEnvironments.getApiUrl()}my_data/libraries/import/`, form, config);
      if (res.data?.code !== 1) throw new Error(res.data?.message || "단가집에 올리지 못했습니다.");
      return res.data.result;
    } catch (error: any) {
      const body = error?.response?.data;
      if (body && body.message) throw Object.assign(new Error(body.message), { reason: body.reason });
      if (error?.response) throw new Error(`단가집에 올리지 못했습니다. (오류 ${error.response.status})`);
      if (error instanceof Error && !("request" in error)) throw error;
      throw new Error("서버와 연결하지 못했습니다. 잠시 뒤 다시 시도해 주세요.");
    }
  },
  // 타사 적산 파일 → 콘엑스 양식 엑셀(설계내역 '엑셀파일 다운로드'와 같은 갑지·원가계산서·내역서·일위대가·단가산출…) 내려받기.
  // 서버가 프로젝트를 만들지 않고 변환만 한다. 다산 묶음(zip, 여러 .estx)은 프로젝트별 xlsx 를 담은 zip 이 온다.
  convertEstimationToExcel: async (file: File) => {
    const key = util.getCookie(Cookies.authKey);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(
        `${GlobalEnvironments.getApiUrl()}project/import/estimation-file?target=conax_excel`,
        form,
        { headers: { Authorization: `Token ${key}` }, responseType: "blob" }
      );
      const cd: string = res.headers["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const isZip = (res.headers["content-type"] || "").includes("zip");
      const base = file.name.replace(/\.[^.]+$/, "");
      const filename = m ? decodeURIComponent(m[1]) : `${base}_콘엑스.${isZip ? "zip" : "xlsx"}`;
      return { blob: res.data as Blob, filename };
    } catch (error: any) {
      // 오류 본문도 blob 으로 오므로 JSON 메시지를 꺼낸다
      let message = "";
      try {
        const text = await (error?.response?.data as Blob).text();
        message = JSON.parse(text).message || "";
      } catch (e) {}
      throw new Error(message || "파일을 변환하지 못했습니다. 파일을 다시한번 확인해 주세요.");
    }
  },
  makeQueryString: (args: any) => {
    if (!args) {
      return "";
    } else {
      let queryString = [];
      for (const key of Object.keys(args)) {
        queryString.push(`${key}=${args[key]}`);
      }
      return `?${queryString.join("&")}`;
    }
  },
  uploadProject: (args: {
    copy: any;
    normal: any;
    contract: any;
    change: any;
    ready: any;
    complete: any;
    estimate: any;
  }, onUploadProgress?: (percent: number) => void) => {
    const { copy, normal, contract, change, ready, complete, estimate } = args;
    const key = util.getCookie(Cookies.authKey);
    var config: any = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    // 큰 파일(수십 MB)은 올리는 데만 몇 분 걸린다 — 화면이 진행률을 보여줄 수 있게 넘겨준다
    if (onUploadProgress) {
      config.onUploadProgress = (e: any) => {
        if (e && e.total) onUploadProgress(Math.floor((e.loaded / e.total) * 100));
      };
    }
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}project/download`,
          {
            copy,
            normal,
            contract,
            change,
            ready,
            complete,
            estimate,
          },
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createProject: (
    title: string,
    tempRow: boolean,
    estimate?: boolean,
    copy?: boolean,
    corporationType?: string,
    memo?: string,
    template?: string
  ) => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}project/`,
          {
            projectName: title,
            type: estimate ? "estimate" : copy ? "copy" : "normal",
            tempRow: tempRow,
            corporationType,
            memo,
            template: template || "일반",
          },
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // 커스텀 견적서 생성 — 문서+프로젝트(type=custom_estimate)가 함께 만들어진다.
  // 응답 result.project_id 로 dk-cost 커스텀 견적 편집기 딥링크 이동.
  createCustomEstimateDoc: (title: string) => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return axios.post(
      `${GlobalEnvironments.getApiUrl()}custom_estimate/doc`,
      { name: title, columns: [], total_scheme: {}, nodes: [] },
      config
    );
  },
  /** B2B 포털(광해 등) 소속 여부·역할. 소속이 없으면 me 가 없다. 실패해도 일반 화면에 영향 없게 호출부가 삼킨다. */
  getTenantBootstrap: (): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    return axios.get(`${GlobalEnvironments.getApiUrl()}tenant/bootstrap/`, {
      headers: { Authorization: `Token ${key}` },
    });
  },
  getMyInfo: (): Promise<AxiosResponse<UserInfoModel>> => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return axios.get(`${GlobalEnvironments.getApiUrl()}user/info`, config);
  },
  loginWithOption: (
    id: string,
    pw: string,
    autoLogin: boolean,
    saveId: boolean
  ) => {
    const now = new Date();
    saveId
      ? util.setCookie({
          cookieName: Cookies.loginId,
          cookieValue: id,
          cookieExpire: new Date(now.setFullYear(now.getFullYear() + 1)),
        })
      : util.deleteCookie(Cookies.loginId);

    return new Promise<any>(async (resolve, reject) => {
      try {
        const res = await api.loginAsync(id, pw);
        const key = res.data.key;
        if (autoLogin)
          util.setCookie({
            cookieName: Cookies.authKey,
            cookieValue: key,
            cookieExpire: new Date(now.setFullYear(now.getFullYear() + 1)),
            cookieDomain: GlobalEnvironments.getDomainUrl(),
            cookiePath: "/",
          });
        else
          util.setCookie({
            cookieName: Cookies.authKey,
            cookieValue: key,
            cookieDomain: GlobalEnvironments.getDomainUrl(),
            cookiePath: "/",
          });
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  },
  loginAsync: async (id: string, pw: string) => {
    return await axios.post(`${GlobalEnvironments.getApiUrl()}login/`, {
      username: id,
      password: pw,
    });
  },
  socialLogin: async (provider: string, code: string, redirectUrl: string) => {
    //
    const res = await axios.post(
      `${GlobalEnvironments.getApiUrl()}login/social/${provider}`,
      {
        code,
        redirectUrl,
      }
    );

    return res;
  },
  socialSignUp: async (provider: string, providerId: string, impId: string) => {
    //
    const res = await axios.post(
      `${GlobalEnvironments.getApiUrl()}user/signup/${provider}`,
      {
        provider,
        providerId,
        impId,
      }
    );

    return res;
  },
  getProjects: (projectType: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}project/?project_type=${projectType}`,
          config
        )
        .then((result) => {
          const projects = [];
          for (const p of result.data.projects) {
            projects.push(new Project(p));
          }
          resolve(projects);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createEtcProject: (args: {
    id: number;
    type: string;
    originId: number;
  }): Promise<any> => {
    const { id, type, originId } = args;
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}project/etc`,
          { id, type, originId },
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getProjectInfo: (args: any): Promise<any> => {
    const { projectId } = args;
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}project/statement?projectId=${projectId}`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  saveProject: (args: any) => {
    const {
      projectId,
      projectName,
      statements,
      priceCalculations,
      laborPrices,
      itemizeCosts,
      machinePrices,
      materialPrices,
      invoiceRate,
      projectInfo,
      permissions,
      exchangeRate,
      wageFactor,
      decimalPoint,
      etc,
      readyLength,
      projectStartDate,
      projectEndDate,
    } = args;
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise(async (resolve, reject) => {
      axios
        .put(
          `${GlobalEnvironments.getApiUrl()}project/save`,
          {
            projectName,
            projectId,
            statements,
            priceCalculations,
            laborPrices,
            itemizeCosts,
            machinePrices,
            materialPrices,
            invoiceRate,
            projectInfo,
            permissions,
            exchangeRate,
            wageFactor,
            decimalPoint,
            etc,
            readyLength,
            projectStartDate,
            projectEndDate,
          },
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteProject: (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .delete(
          `${GlobalEnvironments.getApiUrl()}project/?project_id=${id}`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getXmlProject: (id: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id,
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(`${GlobalEnvironments.getApiUrl()}export_xml`, config)
        .then((result) => {
          resolve(result?.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getRelativeProjects: (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const args = api.makeQueryString({ project_id: id });
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(`${GlobalEnvironments.getApiUrl()}project/${args}`, config)
        .then((result) => {
          const projects = [];
          for (const p of result.data.projects) {
            projects.push(new Project(p));
          }
          resolve(projects);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  checkEmailAsync: async (email: string) => {
    const config: AxiosRequestConfig = {
      params: {
        email,
      },
    };
    return await new Promise(async (resolve, reject) => {
      try {
        const res = await axios.get(
          `${GlobalEnvironments.getApiUrl()}user/check`,
          config
        );
        resolve(res.data.result);
      } catch (error) {
        reject(error);
      }
    });
  },
  checkPasswordAsync: async (password: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await await axios.post(
      `${GlobalEnvironments.getApiUrl()}user/checkPw/`,
      {
        password,
      },
      config
    );
  },
  postWorkCompany: (values: any) => {
    //업체정보 등록
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return axios.post(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_company`,
      {
        companyName: values.companyName,
        representativeName: values.representativeName,
        corporateRegistNum: values.corporateRegistNum,
        corporateNum: values.corporateNum,
        businessAddress: values.businessAddress,
        typeOfBusiness: values.typeOfBusiness,
        phoneNum: values.phoneNum,
        faxNum: values.faxNum,
        writer: values.writer,
        bankName: values.bankName,
        accountNum: values.accountNum,
        accountHolder: values.accountHolder,
        typeOfDeposit: values.typeOfDeposit,
        storeName: values.storeName,
      },
      config
    );
  },

  getWorkCompanyList: (args: any) => {
    //업체 리스트 정보 불러오기 (여러개)
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const offset = args.offset || 0;
    const limit = args.limit || 40;
    const queryString = api.makeQueryString({ offset, limit });

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_company${queryString}`,
      config
    );
  },

  editProjectInfo: (project: Project) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .put(
          `${GlobalEnvironments.getApiUrl()}project/`,
          {
            project: {
              id: project.id,
              name: project.name?.trim(),
              companyName: project.companyName,
              clientName: project.clientName,
              corporationType: project.corporationType,
              memo: project.memo,
              startDate: project.startDate
                ? project.startDate.format("yyyy-MM-dd HH:mm:ss")
                : null,
              endDate: project.endDate
                ? project.endDate.format("yyyy-MM-dd HH:mm:ss")
                : null,
              template: project.template,
            },
          },
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  putWorkCompany: (object: any, idState: any) => {
    //업체 정보 수정
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.put(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_company`,
      {
        companyName: object.companyName,
        representativeName: object.representativeName,
        corporateRegistNum: object.corporateRegistNum,
        corporateNum: object.corporateNum,
        businessAddress: object.businessAddress,
        typeOfBusiness: object.typeOfBusiness,
        phoneNum: object.phoneNum,
        faxNum: object.faxNum,
        writer: object.writer,
        bankName: object.bankName,
        accountNum: object.accountNum,
        accountHolder: object.accountHolder,

        typeOfDeposit: object.typeOfDeposit,
        storeName: object.storeName,

        company_id: idState,
      },
      config
    );
  },

  deleteWorkCompany: (idState: any) => {
    //업체 정보 삭제
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const company_id = idState;
    const queryString = api.makeQueryString({ company_id });

    return axios.delete(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_company${queryString}`,
      config
    );
  },

  postDeputy: (values: any) => {
    //현장대리인 등록
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.post(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_deputy`,
      {
        deputyName: values.deputyName,
        deputyResidentNum: values.deputyResidentNum,
        deputyAddress: values.deputyAddress,
        deputyPosition: values.deputyPosition,
        deputyEvent: values.deputyEvent,
        deputyGrade: values.deputyGrade,
        deputyRegistNum: values.deputyRegistNum,
        deputyRegistDate: values.deputyRegistDate,

        deputyGetDate: values.deputyGetDate,
        deputyRenewDate: values.deputyRenewDate,
        deputyJoinDate: values.deputyJoinDate,
        deputyWorkPlace: values.deputyWorkPlace,
      },
      config
    );
  },

  getDeputyList: (args: any) => {
    //현장대리인 리스트 정보 불러오기 (여러개)
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const offset = args.offset || 0;
    const limit = args.limit || 40;
    const queryString = api.makeQueryString({ offset, limit });

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_deputy${queryString}`,
      config
    );
  },

  putDeputy: (object: any, idState: any) => {
    //현장대리인 수정
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return axios.put(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_deputy`,
      {
        deputyName: object.deputyName,
        deputyResidentNum: object.deputyResidentNum,
        deputyAddress: object.deputyAddress,
        deputyPosition: object.deputyPosition,
        deputyEvent: object.deputyEvent,
        deputyGrade: object.deputyGrade,
        deputyRegistNum: object.deputyRegistNum,
        deputyRegistDate: object.deputyRegistDate,
        deputy_id: idState,

        deputyGetDate: object.deputyGetDate,
        deputyRenewDate: object.deputyRenewDate,
        deputyJoinDate: object.deputyJoinDate,
        deputyWorkPlace: object.deputyWorkPlace,
      },
      config
    );
  },

  deleteDeputy: (idState: any) => {
    //현장대리인 삭제
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const deputy_id = idState;
    const queryString = api.makeQueryString({ deputy_id });

    return axios.delete(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_deputy${queryString}`,
      config
    );
  },

  postWorklist: (object: any) => {
    //나의 업무목록 등록
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return axios.post(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_list`,
      {
        contractKind: object.contractKind,
        constructionName: object.constructionName,
        typeOfWork: object.typeOfWork,

        companyName: object.companyName,
        representativeName: object.representativeName,
        corporateRegistNum: object.corporateRegistNum,
        corporateNum: object.corporateNum,
        businessAddress: object.businessAddress,
        typeOfBusiness: object.typeOfBusiness,
        phoneNum: object.phoneNum,
        faxNum: object.faxNum,
        writer: object.writer,
        bankName: object.bankName,
        accountNum: object.accountNum,
        accountHolder: object.accountHolder,

        deputyName: object.deputyName,
        deputyResidentNum: object.deputyResidentNum,
        deputyAddress: object.deputyAddress,
        deputyPosition: object.deputyPosition,
        deputyEvent: object.deputyEvent,
        deputyGrade: object.deputyGrade,
        deputyRegistNum: object.deputyRegistNum,
        deputyRegistDate: object.deputyRegistDate,

        contractDate: object.contractDate,
        GuaranteeDate: object.GuaranteeDate,
        claimDate: object.claimDate,
        beginDate: object.beginDate,
        completionDate: object.completionDate,
        realCompletionDate: object.realCompletionDate,
        completionExpectDate: object.completionExpectDate,

        deputyGetDate: object.deputyGetDate,
        deputyRenewDate: object.deputyRenewDate,
        deputyJoinDate: object.deputyJoinDate,
        deputyWorkPlace: object.deputyWorkPlace,
        contractRegion: object.contractRegion,
        contractRepairRate: object.contractRepairRate,
        compensationOfDeferment: object.compensationOfDeferment,
        contractRepairTerm: object.contractRepairTerm,
        storeName: object.storeName,
        typeOfDeposit: object.typeOfDeposit,

        safetyManagePlan: object.safetyManagePlan,
        safetyManageState: object.safetyManageState,
        enviManagePlan: object.enviManagePlan,
        enviManageState: object.enviManageState,
        photoListContent: object.photoListContent,
      },
      config
    );
  },

  getWorklists: async (args: any) => {
    //나의 업무목록 리스트 정보 불러오기 (여러개)
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const offset = args.offset || 0;
    const limit = args.limit || 40;
    const queryString = api.makeQueryString({ offset, limit });
    return await new Promise(async (resolve, reject) => {
      try {
        const res = axios.get(
          `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_list${queryString}`,
          config
        );
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  },

  getWorklistImages: async (args: { worklist_id: number }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const { worklist_id } = args;
    const queryString = api.makeQueryString({ worklist_id });

    //axios.get(
    return await new Promise(async (resolve, reject) => {
      try {
        const res = await axios.get(
          `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_image${queryString}`,
          config
        );
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  },

  uploadWorklistImage: async (args: {
    imageFile: any;
    worklist_id: number;
    image_number: number;
    image_sub_number: number;
  }) => {
    const { imageFile, worklist_id, image_number, image_sub_number } = args;
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    const form = new FormData();
    form.append("imageFile", imageFile);
    form.append("worklist_id", worklist_id.toString());
    form.append("image_number", image_number.toString());
    form.append("image_sub_number", image_sub_number.toString());
    return await new Promise(async (resolve, reject) => {
      try {
        const res = await axios.post(
          `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_image`,
          form,
          config
        );
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  },

  deleteWorklistImage: (boardImageId: number) => {
    //이미지 삭제
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const board_image_id = boardImageId;
    const queryString = api.makeQueryString({ board_image_id });
    return axios.delete(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_image${queryString}`,
      config
    );
  },

  getWorklist: (args: { id: number }) => {
    //나의 업무목록 리스트 정보 불러오기 (하나)
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const worklist_id = args.id;
    const queryString = api.makeQueryString({ worklist_id });
    return axios.get(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_list${queryString}`,
      config
    );
  },

  putWorklist: (object: any, idState: any) => {
    //나의 업무목록 수정
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.put(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_list`,
      {
        constructionName: object.constructionName,
        organName: object.organName,
        orderOrganization: object.orderOrganization,
        constructionAddress: object.constructionAddress,
        contractDate: object.contractDate,
        contractPrice: object.contractPrice,
        contractGuaranteePrice: object.contractGuaranteePrice,
        GuaranteeDate: object.GuaranteeDate,
        claimPrice: object.claimPrice,
        claimDate: object.claimDate,
        beginDate: object.beginDate,
        completionDate: object.completionDate,
        completionCalPrice: object.completionCalPrice,
        realCompletionDate: object.realCompletionDate,
        dear: object.dear,
        companyName: object.companyName,
        representativeName: object.representativeName,
        corporateRegistNum: object.corporateRegistNum,
        corporateNum: object.corporateNum,
        businessAddress: object.businessAddress,
        typeOfWork: object.typeOfWork,
        typeOfBusiness: object.typeOfBusiness,
        phoneNum: object.phoneNum,
        faxNum: object.faxNum,
        writer: object.writer,
        bankName: object.bankName,
        accountNum: object.accountNum,
        accountHolder: object.accountHolder,
        depositType: object.depositType,
        deputyName: object.deputyName,
        deputyResidentNum: object.deputyResidentNum,
        deputyAddress: object.deputyAddress,
        deputyPosition: object.deputyPosition,
        deputyEvent: object.deputyEvent,
        deputyGrade: object.deputyGrade,
        deputyRegistNum: object.deputyRegistNum,
        deputyRegistDate: object.deputyRegistDate,
        completionExpectDate: object.completionExpectDate,
        contractKind: object.contractKind,

        image: object.image,

        deputyGetDate: object.deputyGetDate,
        deputyRenewDate: object.deputyRenewDate,
        deputyJoinDate: object.deputyJoinDate,
        deputyWorkPlace: object.deputyWorkPlace,
        contractRegion: object.contractRegion,
        contractRepairRate: object.contractRepairRate,
        compensationOfDeferment: object.compensationOfDeferment,
        contractRepairTerm: object.contractRepairTerm,
        storeName: object.storeName,
        typeOfDeposit: object.typeOfDeposit,

        safetyManagePlan: object.safetyManagePlan,
        safetyManageState: object.safetyManageState,
        enviManagePlan: object.enviManagePlan,
        enviManageState: object.enviManageState,
        photoListContent: object.photoListContent,

        expensedSafetyPlanCost: object.expensedSafetyPlanCost,
        expensedSafetyStateCost: object.expensedSafetyStateCost,
        expensedEnviPlanCost: object.expensedEnviPlanCost,
        expensedEnviStateCost: object.expensedEnviStateCost,

        worklist_id: idState,
      },
      config
    );
  },

  deleteWorklist: (idState: any) => {
    //나의 업무목록 삭제
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const worklist_id = idState;
    const queryString = api.makeQueryString({ worklist_id });

    return axios.delete(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/work_list${queryString}`,
      config
    );
  },

  logout: () => {
    //로그아웃
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    util.deleteCookie(Cookies.authKey);
    return axios.post(
      `${GlobalEnvironments.getApiUrl()}rest-auth/logout/`,
      {},
      config
    );
  },
  viewAllAlarms: (): Promise<any> => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise(async (resolve, reject) => {
      axios
        .put(`${GlobalEnvironments.getApiUrl()}alarm/`, {}, config)
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  viewAlarm: (args: { id: number }): Promise<any> => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const { id } = args;
    return new Promise(async (resolve, reject) => {
      axios
        .put(`${GlobalEnvironments.getApiUrl()}alarm/`, { id }, config)
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  getNoticesAsync: async (offset: number, limit: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config: AxiosRequestConfig = {
      params: {
        offset,
        limit,
      },
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await new Promise(async (resolve, reject) => {
      try {
        const res = await axios.get(
          `${GlobalEnvironments.getApiUrl()}notice/`,
          config
        );
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  },

  getAlarmsAsync: async (offset: number, limit: number, view?: boolean) => {
    const key = util.getCookie(Cookies.authKey);
    const config: AxiosRequestConfig = {
      params: {
        offset,
        limit,
        view,
      },
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await new Promise(async (resolve, reject) => {
      try {
        const res = await axios.get(
          `${GlobalEnvironments.getApiUrl()}alarm/`,
          config
        );
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  },

  getNotice: (args: { id: number }) => {
    //공지사항 목록 불러오기 (하나)
    const id = args.id;
    const queryString = api.makeQueryString({ id });
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}notice${queryString}`,
      config
    );
  },

  getNotices: (args: any) => {
    //공지사항 목록 불러오기 (여러개)
    const offset = args.offset || 0;
    const limit = args.limit || 10;
    const queryString = api.makeQueryString({ offset, limit });

    return axios.get(`${GlobalEnvironments.getApiUrl()}notice${queryString}`);
  },

  getEstimate: () => {
    //용역의뢰 페이지 홍보도표 목록 불러오기
    return axios.get(`${GlobalEnvironments.getApiUrl()}estimate_service`);
  },

  getCompanyNames: () => {
    return axios.get(`${GlobalEnvironments.getApiUrl()}user/companies`);
  },

  postConstructionImg: (values: any) => {
    //사진대지 등록
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return axios.post(
      `${GlobalEnvironments.getApiUrl()}auto_work/work_project/image_upload`,
      {
        imageFile: values.photo11,
      },
      config
    );
  },
  postInquiry: (values: any) => {
    const key = util.getCookie(Cookies.authKey);
    let config;
    if (key) {
      config = {
        headers: {
          Authorization: `Token ${key}`,
          "Content-Type": `mulripart/form-data`,
        },
      };
    } else {
      config = {
        headers: {
          "Content-Type": `mulripart/form-data`,
        },
      };
    }

    return axios.post(
      `${GlobalEnvironments.getApiUrl()}inquiry/`,
      values,
      config
    );
  },
  postEducationApplication: (values: any) => {
    const key = util.getCookie(Cookies.authKey);
    let config;
    if (key) {
      config = {
        headers: {
          Authorization: `Token ${key}`,
          "Content-Type": `multipart/form-data`,
        },
      };
    } else {
      config = {
        headers: {
          "Content-Type": `multipart/form-data`,
        },
      };
    }

    return axios.post(
      `${GlobalEnvironments.getApiUrl()}education-application/`,
      values,
      config
    );
  },
  getMyInquiry: (limit: number, offset: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}inquiry/my?limit=${limit}&offset=${offset}`,
      config
    );
  },
  getMyInquiryDetail: (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}inquiry/my?id=${id}`,
      config
    );
  },
  getMyInquiryReplyDetail: (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}inquiry/my_reply?id=${id}`,
      config
    );
  },
  postRequestApplication: (values: any) => {
    const config = {
      headers: {
        "Content-Type": `mulripart/form-data`,
      },
    };

    return axios.post(
      `${GlobalEnvironments.getApiUrl()}request_application/`,
      values,
      config
    );
  },
  getAuthUserInfoAsync: async (email: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      params: {
        username: email,
      },
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}rest-auth/user`,
      config
    );
  },
  // bug: 현재는 password_reset_confirm 오류때문에 정상동작 불가.
  resetPasswordAsync: async (email: string) => {
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}rest-auth/password/reset/`,
      {
        email,
      }
    );
  },
  changePasswordAsync: async (new_password: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}rest-auth/password/change/`,
      {
        new_password1: new_password,
        new_password2: new_password,
      },
      config
    );
  },
  // bug: 현재는 password_reset_confirm 오류때문에 정상동작 불가.
  resetConfirmPasswordAsync: async (uid: string, new_password: string) => {
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}rest-auth/password/reset/confirm/`,
      {
        uid,
        token: "sad",
        new_password1: new_password,
        new_password2: new_password,
      }
    );
  },
  postPaymentCompleteAsync: async (res: ImpCallbackResponseModel) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}payment/complete/`,
      res,
      config
    );
  },
  getPaymentProductsAsync: async () => {
    const config = {
      params: {
        is_test: GlobalEnvironments.getIsGetTestProduct(),
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}payment/product`,
      config
    );
  },
  
  // 구독 상품 조회 API (인증 없이 공개 접근)
  getSubscriptionProductsAsync: async () => {
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}payment/subscription/products/`
    );
  },
  // 구독 가격 검증 API
  validateSubscriptionPriceAsync: async (data: {
    product_ids: number[];
    is_yearly: boolean;
    is_subscription: boolean;
    total_price: number;
  }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}payment/validate-price/`,
      data,
      config
    );
  },
  
  // 정기결제 상태 조회 API
  getSubscriptionStatusAsync: async () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}payment/subscription/status/`,
      config
    );
  },
  
  // 정기결제 해지 API
  cancelSubscriptionAsync: async (data: {
    cancellation_reason: string;
    cancellation_detail?: string;
  }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}payment/subscription/cancel/`,
      data,
      config
    );
  },
  
  getPaymentHistoryAsync: async (offset?: number, limit?: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        offset,
        limit,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}payment/history/`,
      config
    );
  },
  postWithdrawalAsync: async (reason?: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}user/withdrawal/`,
      {
        reason: reason,
      },
      config
    );
  },
  postCertificationIsNotDuplicateAsync: async (model: any) => {
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}user/certification/`,
      model
    );
  },
  postFindIdAsync: async (data: any) => {
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}user/findId/`,
      data
    );
  },
  postFindPwAsync: async (data: any) => {
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}user/findPw/`,
      data
    );
  },
  postChangePwInFindAccountAsync: async (password: string, token: string) => {
    const config = {
      headers: {
        Authorization: `Token ${token}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}rest-auth/password/change/`,
      {
        new_password1: password,
        new_password2: password,
      },
      config
    );
  },
  postUserRegistrationAsync: async (data: any) => {
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}rest-auth/registration/`,
      data
    );
  },
  getCouponHistoryAsync: async (offset?: number, limit?: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        offset,
        limit,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}coupon/history`,
      config
    );
  },
  postRegistCouponAsync: async (couponKey: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}coupon/history`,
      {
        coupon_key: couponKey,
      },
      config
    );
  },
  putCouponUseAsync: async (couponKey: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.put(
      `${GlobalEnvironments.getApiUrl()}coupon/history/`,
      {
        coupon_key: couponKey,
      },
      config
    );
  },
  getCouponUsableAsync: async (key: string) => {
    const config = {
      params: {
        coupon_key: key,
      },
    };
    return await axios.get(`${GlobalEnvironments.getApiUrl()}coupon`, config);
  },
  getCardNewsAsync: async (offset: number, limit: number) => {
    const config = {
      params: {
        offset,
        limit,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}notice/card_news`,
      config
    );
  },
  getLectureAsync: async () => {
    return await axios.get(`${GlobalEnvironments.getApiUrl()}lecture`);
  },
  getLectureCategoryAsync: async () => {
    return await axios.get(`${GlobalEnvironments.getApiUrl()}lecture/category`);
  },
  getSampleStatement: async () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}sample_statement`,
      config
    );
  },
  getSampleStatementDetail: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id: id,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}sample_statement/detail`,
      config
    );
  },
  postSampleStatemnetRecommend: async (arg: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}sample_statement/recommend/`,
      arg,
      config
    );
  },
  getSampleStatementIsRecommend: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id: id,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}sample_statement/recommend`,
      config
    );
  },
  deleteSampleStatementRecommend: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id: id,
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}sample_statement/recommend`,
      config
    );
  },
  getFilesFromUrl: async (url: string) => {
    return await axios.get(url);
  },
  deleteSampleStatement: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id: id,
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}sample_statement/detail`,
      config
    );
  },
  getUserBoardList: async () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}user_board`,
      config
    );
  },
  getUserBoardDetail: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id: id,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}user_board/detail`,
      config
    );
  },
  // postUserBoardRecommend: async (arg: any) => {
  //   const key = util.getCookie(Cookies.authKey);
  //   const config = {
  //     headers: {
  //       Authorization: `Token ${key}`,
  //     },
  //   };
  //   return await axios.post(
  //     `${GlobalEnvironments.getApiUrl()}user_board/recommend/`,
  //     arg,
  //     config
  //   );
  // },
  // getUserBoardIsRecommend: async (id: number) => {
  //   const key = util.getCookie(Cookies.authKey);
  //   const config = {
  //     headers: {
  //       Authorization: `Token ${key}`,
  //     },
  //     params: {
  //       id: id,
  //     },
  //   };
  //   return await axios.get(
  //     `${GlobalEnvironments.getApiUrl()}user_board/recommend`,
  //     config
  //   );
  // },
  // deleteUserBoardRecommend: async (id: number) => {
  //   const key = util.getCookie(Cookies.authKey);
  //   const config = {
  //     headers: {
  //       Authorization: `Token ${key}`,
  //     },
  //     params: {
  //       id: id,
  //     },
  //   };
  //   return await axios.delete(
  //     `${GlobalEnvironments.getApiUrl()}user_board/recommend`,
  //     config
  //   );
  // },
  deleteUserBoard: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id: id,
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}user_board/detail`,
      config
    );
  },
  uploadFileAsync: async (file: File, path: string = "ddusul") => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const form = new FormData();
    form.append("file", file);
    path && form.append("path", path);
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}upload/`,
      form,
      config
    );
  },
  getBusinessNumberStatus: async (businessNumber: string) => {
    const config = {
      headers: {
        Authorization: businessApiKey,
      },
    };
    // 국세청(odcloud) API가 간헐적으로 503을 반환하므로 5xx/네트워크 오류는 재시도
    let lastError;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        return await axios.post(
          `https://api.odcloud.kr/api/nts-businessman/v1/status?serviceKey=${businessApiKey}`,
          {
            b_no: [businessNumber],
          },
          config
        );
      } catch (e: any) {
        lastError = e;
        const status = e?.response?.status;
        if (status && status < 500) {
          throw e;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    throw lastError;
  },
  getBusinessNumberDuplicate: async (businessNumber: string) => {
    const config = {
      params: {
        businessNumber: businessNumber,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}user/business`,
      config
    );
  },
  getMarketingInfo: async () => {
    return await axios.get(`${GlobalEnvironments.getApiUrl()}stats`);
  },
  postServiceRequestDoc: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    let config;
    if (key) {
      config = {
        headers: {
          Authorization: `Token ${key}`,
        },
      };
    } else {
      config = {
        headers: {},
      };
    }
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}service_request_doc/`,
      data,
      config
    );
  },
  getMyServiceRequestDoc: async (limit: number, offset: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}service_request_doc/my?limit=${limit}&offset=${offset}`,
      config
    );
  },
  getMyServiceRequestDocDetail: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };

    return axios.get(
      `${GlobalEnvironments.getApiUrl()}service_request_doc/my?id=${id}`,
      config
    );
  },
  getDownloadDataSearch: async (
    filename: string,
    offset?: number,
    limit?: number
  ) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        filename,
        offset,
        limit,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}download/search`,
      config
    );
  },
  getDownloadDataLink: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        file_id: id,
      },
    };
    return await axios.get(`${GlobalEnvironments.getApiUrl()}download`, config);
  },
  getDownloadDataDetail: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}download?id=${id}`,
      config
    );
  },
  likeDownloadData: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}download/like`,
      { post_id: id },
      config
    );
  },
  unLikeDownloadData: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}download/like?post_id=${id}`,
      config
    );
  },
  postDownloadDataComment: async (id: number, comment: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}download/comment`,
      { post_id: id, comment: comment },
      config
    );
  },
  deleteDownloadDataComment: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}download/comment?comment_id=${id}`,
      config
    );
  },
  postNoticeComment: async (id: number, text: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}notice/comment`,
      { notice_id: id, text: text },
      config
    );
  },
  deleteNoticeComment: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}notice/comment?comment_id=${id}`,
      config
    );
  },
  getSheetNamesAsync: async (files: string[]) => {
    const key = util.getCookie(Cookies.authKey);
    const params = new URLSearchParams();
    files.forEach((file) => {
      params.append("files", file);
    });
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params,
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}excel/X2/sheets`,
      config
    );
  },
  postExcelX2Execute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/X2/execute`,
      data,
      config
    );
  },
  postExcelContractExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/contract/execute`,
      data,
      config
    );
  },
  postExcelReadyExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/ready/execute`,
      data,
      config
    );
  },

  postExcelDeleteExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/delete/execute`,
      data,
      config
    );
  },
  postExcelOverrideExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/override/execute`,
      data,
      config
    );
  },
  postExcelColorExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/color/execute`,
      data,
      config
    );
  },
  postExcelFluctuationExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/fluctuation/execute`,
      data,
      config
    );
  },
  postExcelPhasedStatementExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/phased/execute`,
      data,
      config
    );
  },
  postExcelChangeReportExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/report/execute`,
      data,
      config
    );
  },
  toggleFavoriteProject: async (id: number, isFavorite: boolean) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}project/favorite`,
      { id: id, is_favorite: isFavorite },
      config
    );
  },
  getServiceRequestDocReview: async (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        id: id,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}service_request_doc/review`,
      config
    );
  },
  postServiceRequestDocReview: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    let config;
    config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}service_request_doc/review`,
      data,
      config
    );
  },

  getCommunityPosts: async (
    category: number,
    offset: number,
    limit: number,
    searchTerm?: string,
    sortBy: "latest" | "popular" = "latest"
  ): Promise<{ posts: Post[]; pinnedPosts: Post[]; total: number }> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}community/posts/`, {
        ...config,
        params: { category, offset, limit, search: searchTerm, sortBy: sortBy },
      })
      .then((response) => {
        const { posts, pinned_posts, total } = response.data;
        const transformedPosts = posts.map((post: any) =>
          util.transformSnakeToCamelCase<any, Post>(post)
        );
        const transformedPinnedPosts = (pinned_posts || []).map((post: any) =>
          util.transformSnakeToCamelCase<any, Post>(post)
        );
        return {
          posts: transformedPosts,
          pinnedPosts: transformedPinnedPosts,
          total: total,
        };
      });
  },

  createCommunityPost: async (postData: FormData): Promise<Post> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "multipart/form-data",
      },
    };

    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}community/posts/create/`,
        postData,
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, Post>(response.data)
      )
      .catch((error: any) => {
        throw error;
      });
  },

  getCommunityPostDetail: async (postId: number): Promise<Post> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(
        `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/`,
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, Post>(response.data)
      );
  },

  updateCommunityPost: async (
    postId: number,
    postData: FormData | { title: string; content: string }
  ): Promise<Post> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        ...(postData instanceof FormData
          ? {}
          : { "Content-Type": "application/json" })
      },
    };
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/update/`,
        postData,
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, Post>(response.data)
      )
      .catch((error: any) => {
        throw error;
      });
  },

  deleteCommunityPost: async (postId: number): Promise<void> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/delete/`,
      config
    );
  },

  createCommunityComment: async (
    postId: number,
    commentData: { content: string }
  ): Promise<Comment> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/comments/`,
        commentData,
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, Comment>(response.data)
      );
  },

  deleteCommunityComment: async (
    postId: number,
    commentId: number
  ): Promise<void> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/comments/${commentId}/`,
      config
    );
  },

  toggleCommunityPostLike: async (
    postId: number
  ): Promise<{ likes: number }> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/like/`,
        {},
        config
      )
      .then((response) => response.data);
  },

  reportCommunityPost: async (
    postId: number,
    reportData: { reason: string }
  ): Promise<void> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/report/`,
      reportData,
      config
    );
  },

  getCommunityComments: async (
    postId: number,
    offset: number,
    limit: number
  ): Promise<{ comments: Comment[]; total: number }> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(
        `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/comments/`,
        {
          ...config,
          params: { offset, limit },
        }
      )
      .then((response) => response.data);
  },

  getCommunityCategories: async (): Promise<any> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}community/categories/`, config)
      .then((response) => response.data);
  },

  // AI 게시글 목록 가져오기
  getAIPosts: async (
    offset: number,
    limit: number,
    searchTerm?: string,
    sortBy: "latest" | "popular" = "latest"
  ): Promise<{ posts: AIPost[]; total: number }> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}community/ai/posts/`, {
        ...config,
        params: { offset, limit, search: searchTerm, sortBy: sortBy },
      })
      .then((response) => {
        const { posts, total } = response.data;
        const transformedPosts = posts.map((post: any) =>
          util.transformSnakeToCamelCase<any, AIPost>(post)
        );
        return {
          posts: transformedPosts,
          total: total,
        };
      });
  },

  // AI 게시글 상세 정보 가져오기
  getAIPostDetail: async (postId: number): Promise<AIPost> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(
        `${GlobalEnvironments.getApiUrl()}community/ai/posts/${postId}/`,
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, AIPost>(response.data)
      );
  },

  // AI 게시글 생성
  createAIPost: async (newPost: {
    title: string;
    content: string;
  }): Promise<AIPost> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };

    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}community/ai/posts/create/`,
        newPost,
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, AIPost>(response.data)
      )
      .catch((error: any) => {
        throw error;
      });
  },

  // AI 게시글 삭제
  deleteAIPost: async (postId: number): Promise<void> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}community/ai/posts/${postId}/delete/`,
      config
    );
  },

  // AI 게시글에 댓글 작성
  createAIPostComment: async (
    postId: number,
    commentData: { content: string }
  ): Promise<AIComment> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}community/ai/posts/${postId}/comments/`,
        commentData,
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, AIComment>(response.data)
      );
  },

  // AI 게시글 좋아요 토글
  toggleAIPostLike: async (postId: number): Promise<{ likes: number }> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}community/ai/posts/${postId}/like/`,
        {},
        config
      )
      .then((response) => response.data);
  },

  // AI 게시글 신고
  reportAIPost: async (
    postId: number,
    reportData: { reason: string }
  ): Promise<void> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}community/ai/posts/${postId}/report/`,
      reportData,
      config
    );
  },

  // AI 답변에 대한 반응
  reactToAIAnswer: async (
    answerId: number,
    reaction: "like" | "dislike"
  ): Promise<AIAnswer> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}community/ai/posts/${answerId}/react/`,
        { reaction },
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, AIAnswer>(response.data)
      );
  },

  // AI 답변에 대한 피드백 제출
  submitAIAnswerFeedback: async (
    answerId: number,
    feedback: string
  ): Promise<void> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}community/ai/posts/${answerId}/feedback/`,
      { feedback },
      config
    );
  },
  getMyPosts: async (
    offset: number = 0,
    limit: number = 10
  ): Promise<{ posts: Post[]; total: number }> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}community/my/posts/`, {
        ...config,
        params: { offset, limit },
      })
      .then((response) => {
        const { posts, total } = response.data;
        const transformedPosts = posts.map((post: any) =>
          util.transformSnakeToCamelCase<any, Post>(post)
        );
        return {
          posts: transformedPosts,
          total: total,
        };
      });
  },

  // 내 댓글 목록 가져오기
  getMyComments: async (
    offset: number = 0,
    limit: number = 10
  ): Promise<{ comments: Comment[]; total: number }> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}community/my/comments/`, {
        ...config,
        params: { offset, limit },
      })
      .then((response) => {
        const { comments, total } = response.data;
        const transformedComments = comments.map((comment: any) =>
          util.transformSnakeToCamelCase<any, Comment>(comment)
        );
        return {
          comments: transformedComments,
          total: total,
        };
      });
  },
  // AI 질의응답 댓글 수정/삭제
  updateAIComment: async (
    postId: number,
    commentId: number,
    content: string
  ): Promise<AIComment> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}community/ai/posts/${postId}/comments/${commentId}/`,
        { content },
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, AIComment>(response.data)
      );
  },
  deleteAIComment: async (postId: number, commentId: number): Promise<void> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}community/ai/posts/${postId}/comments/${commentId}/`,
      config
    );
  },
  // 일반 커뮤니티 댓글 수정
  updateCommunityComment: async (
    postId: number,
    commentId: number,
    content: string
  ): Promise<Comment> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}community/posts/${postId}/comments/${commentId}/`,
        { content },
        config
      )
      .then((response) =>
        util.transformSnakeToCamelCase<any, Comment>(response.data)
      );
  },
  exportExcel: (args: {
    projectId: number;
    projectName: string;
  }): Promise<any> => {
    const key = util.getCookie(Cookies.authKey);
    const config: any = {
      headers: {
        Authorization: `Token ${key}`,
      },
      params: {
        projectId: args.projectId,
      },
      responseType: "blob", // 파일 다운로드를 위한 설정
    };

    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get(
          `${GlobalEnvironments.getApiUrl()}export_excel/`,
          config
        );

        // 파일 다운로드 처리
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${args.projectName}.xlsx`); // 다운로드될 파일명
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  },
  // 프리셋 기반 서류 생성 API 함수들
  postFluctuationReportDocumentExecute: async (data: any) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/document/fluctuation_report`,
      data,
      config
    );
  },

  // 프리셋 기반 서류 생성 API 함수들
  postExcelDocumentExecute: async (data: {
    docType: string;
    presets?: {
      fieldRepresentative: any;
      companyInfo: any;
      projectInfo: any;
    };
    formData?: any; // 안전관리비 사용계획내역 등의 폼 데이터
  }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/document/execute`,
      data,
      config
    );
  },
  // 비동기 엑셀 작업 상태 조회 API
  getExcelTaskStatus: async (taskId: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}excel/status/${taskId}/`,
      config
    );
  },
  getExcelTasks: async () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.get(
      `${GlobalEnvironments.getApiUrl()}excel/tasks/`,
      config
    );
  },
  cancelExcelTask: async (taskId: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/cancel/${taskId}/`,
      {},
      config
    );
  },
  cancelAllExcelTasks: async () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/cancel-all/`,
      {},
      config
    );
  },
  retryExcelTask: async (taskId: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.post(
      `${GlobalEnvironments.getApiUrl()}excel/retry/${taskId}/`,
      {},
      config
    );
  },
  deleteExcelTask: async (taskId: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}excel/tasks/${taskId}/`,
      config
    );
  },
  clearAllExcelTasks: async (status?: string) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    const param = status ? `?status=${status}` : '';
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}excel/tasks/clear/${param}`,
      config
    );
  },
  // Excel 개별 모델 CRUD API 함수들
  // 현장대리인 API
  getFieldRepresentatives: async (): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(
        `${GlobalEnvironments.getApiUrl()}excel/field-representatives/`,
        config
      )
      .then((response) => ({
        ...response,
        data: response.data.data.map((item: any) =>
          util.transformSnakeToCamelCase<any, any>(item)
        ),
      }));
  },

  createFieldRepresentative: async (data: any): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}excel/field-representatives/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  getFieldRepresentative: async (id: number): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    console.log("@@@@");
    return await axios
      .get(
        `${GlobalEnvironments.getApiUrl()}excel/field-representatives/${id}/`,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  updateFieldRepresentative: async (
    id: number,
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}excel/field-representatives/${id}/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  deleteFieldRepresentative: async (
    id: number
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}excel/field-representatives/${id}/`,
      config
    );
  },

  // 회사정보 API
  getCompanyInfos: async (): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/company-info/`, config)
      .then((response) => ({
        ...response,
        data: response.data.data.map((item: any) =>
          util.transformSnakeToCamelCase<any, any>(item)
        ),
      }));
  },

  createCompanyInfo: async (data: any): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}excel/company-info/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  getCompanyInfo: async (id: number): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/company-info/${id}/`, config)
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  updateCompanyInfo: async (
    id: number,
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}excel/company-info/${id}/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  deleteCompanyInfo: async (id: number): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}excel/company-info/${id}/`,
      config
    );
  },

  // 공사정보 API
  getProjectInfos: async (): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/project-info/`, config)
      .then((response) => ({
        ...response,
        data: response.data.data.map((item: any) =>
          util.transformSnakeToCamelCase<any, any>(item)
        ),
      }));
  },

  createProjectInfo: async (data: any): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}excel/project-info/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  getExcelProjectInfo: async (id: number): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/project-info/${id}/`, config)
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  updateExcelProjectInfo: async (
    id: number,
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}excel/project-info/${id}/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  deleteExcelProjectInfo: async (id: number): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}excel/project-info/${id}/`,
      config
    );
  },

  // 안전관리비 사용계획서/내역서 API
  getSafetyPlanDocuments: async (
    projectInfoId?: number
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
      params: projectInfoId ? { projectInfoId } : {},
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/safety-plans/`, config)
      .then((response) => ({
        ...response,
        data: response.data.data.map((item: any) =>
          util.transformSnakeToCamelCase<any, any>(item)
        ),
      }));
  },

  createSafetyPlanDocument: async (data: any): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}excel/safety-plans/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  getSafetyPlanDocument: async (
    type?: string,
    projectInfoId?: number
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
      params: {
        type,
        projectInfoId,
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/safety-plans/`, config)
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  updateSafetyPlanDocument: async (
    id: number,
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    console.log("@@@@[@@@]", data);
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}excel/safety-plans/${id}/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  patchSafetyPlanDocument: async (
    id: number,
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .patch(
        `${GlobalEnvironments.getApiUrl()}/excel/safety-plans/${id}/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  deleteSafetyPlanDocument: async (id: number): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}excel/safety-plans/${id}/`,
      config
    );
  },

  // 환경보전비 사용계획서/내역서 API
  getEnvironmentPlanDocuments: async (
    projectInfoId?: number
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
      params: projectInfoId ? { projectInfoId } : {},
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/environment-plans/`, config)
      .then((response) => ({
        ...response,
        data: response.data.data.map((item: any) =>
          util.transformSnakeToCamelCase<any, any>(item)
        ),
      }));
  },

  createEnvironmentPlanDocument: async (
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .post(
        `${GlobalEnvironments.getApiUrl()}excel/environment-plans/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  getEnvironmentPlanDocument: async (
    type?: string,
    projectInfoId?: number
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
      params: {
        type,
        projectInfoId,
      },
    };
    return await axios
      .get(`${GlobalEnvironments.getApiUrl()}excel/environment-plans/`, config)
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  updateEnvironmentPlanDocument: async (
    id: number,
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .put(
        `${GlobalEnvironments.getApiUrl()}excel/environment-plans/${id}/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  patchEnvironmentPlanDocument: async (
    id: number,
    data: any
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios
      .patch(
        `${GlobalEnvironments.getApiUrl()}excel/environment-plans/${id}/`,
        data,
        config
      )
      .then((response) => ({
        ...response,
        data: util.transformSnakeToCamelCase<any, any>(response.data),
      }));
  },

  deleteEnvironmentPlanDocument: async (
    id: number
  ): Promise<AxiosResponse<any>> => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return await axios.delete(
      `${GlobalEnvironments.getApiUrl()}excel/environment-plans/${id}/`,
      config
    );
  },

  // PortOne V2 빌링키 발급 API (Express Checkout용 - KG이니시스)
  issueBillingKey: async (data: {
    user_info: {
      name?: string;
      email?: string;
      phone?: string;
      provider?: string;
    };
    card_info: {
      cardNumber: string;
      expiryMonth: string;
      expiryYear: string;
      cvc: string;
      birth: string;
      holderName: string;
      phoneNumber?: string;
      email?: string;
    };
    pg_provider?: string;
  }) => {
    const response = await axios.post(
      `${GlobalEnvironments.getApiUrl()}payment/portone/billing-key/`,
      data
    );
    return response.data;
  },

  // PortOne V2 구독 생성 API (Express Checkout용)
  createSubscription: async (data: {
    plan_ids: number[];
    billing_key: string;
    is_yearly: boolean;
    is_subscription: boolean;
    card_info: {
      cardNumber: string;
      expiryMonth: string;
      expiryYear: string;
      cvc: string;
      birth: string;
      holderName: string;
    };
  }) => {
    const response = await axios.post(
      `${GlobalEnvironments.getApiUrl()}payment/portone/subscription/`,
      data
    );
    return response.data;
  },

  // 기존 PortOne V2 구독 결제 관련 API 함수들
  postBillingKeyOnlyAsync: (billingData: {
    card_number: string;
    expiry_year: string;
    expiry_month: string;
    birth?: string;
    business_num?: string;
    pwd_2digit: string;
  }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/billing/`,
          billingData,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // registerBillingKeyAsync wrapper - MyPagePaymentMethod 호환성을 위해 추가
  registerBillingKeyAsync: (billingData: {
    card_number: string;
    expiry_year: string;
    expiry_month: string;
    birth?: string;
    business_num?: string;
    pwd_2digit: string;
  }) => {
    // postBillingKeyOnlyAsync를 호출하여 동일한 기능 제공
    return api.postBillingKeyOnlyAsync(billingData);
  },

  getSubscriptionStatusAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/user/subscription-status/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  postExistingBillingPaymentAsync: (data?: { is_yearly?: boolean; product_ids?: number[] }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/start/`,
          data || {},
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  postSubscriptionCancelAsync: (data?: { cancellation_reason?: string; cancellation_detail?: string; product_id?: number; is_yearly?: boolean }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/cancel/`,
          data || {},
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  postSubscriptionResumeAsync: (data: { product_id: number; is_yearly?: boolean }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/resume/`,
          data,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getSubscriptionSchedulesAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/schedules/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 결제 실패로 재시도 대기 중인 구독을 지금 등록된 카드로 결제 (2026-10-30)
  postSubscriptionRetryNowAsync: (history_id: number) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/retry-now/`,
          { history_id },
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // Phase 2: 중복 구독 체크 API (결제 전 확인용)
  checkSubscriptionOverlapAsync: (data: { product_ids: number[]; is_yearly?: boolean }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/check-overlap/`,
          data,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // Phase 3: 업그레이드 자동 전환 API
  upgradeSubscriptionAsync: (data: { product_ids: number[] }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/upgrade/`,
          data,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getSubscriptionInfoAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/info/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getSubscriptionHistoryAsync: (params?: { product_id?: number; limit?: number; offset?: number }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    
    // 쿼리 파라미터 구성
    const queryParams = new URLSearchParams();
    if (params?.product_id) queryParams.append('product_id', params.product_id.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const queryString = queryParams.toString();
    const url = `${GlobalEnvironments.getApiUrl()}payment/subscription/history/${queryString ? '?' + queryString : ''}`;
    
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(url, config)
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  deleteSubscriptionBillingAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .delete(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/billing/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 카드 변경 (빌링키 업데이트)
  updateSubscriptionBillingAsync: (billingData: {
    card_number: string;
    expiry_year: string;
    expiry_month: string;
    birth?: string;
    business_num?: string;
    pwd_2digit: string;
  }) => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .put(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/billing/`,
          billingData,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getSubscriptionBillingAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    const config = {
      headers: {
        Authorization: `Token ${key}`,
        "Content-Type": "application/json",
      },
    };
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/subscription/billing/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // Phase 6: API 및 쿼리 기능 - 사용자 권한 조회 API
  getUserPermissionsAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/user/permissions/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // Phase 6: API 및 쿼리 기능 - 사용자 구독 상태 조회 API
  getUserSubscriptionStatusAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/user/subscription-status/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 프로모션 관련 API 함수들
  getPromotionStatusAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
        'Content-Type': 'application/json'
      },
    };
    
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/promotion/status/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getPromotionEligibilityAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
        'Content-Type': 'application/json'
      },
    };
    
    return new Promise<any>(async (resolve, reject) => {
      axios
        .get(
          `${GlobalEnvironments.getApiUrl()}payment/promotion/eligibility/`,
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  applyPromotionAsync: (promotionType: string) => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
        'Content-Type': 'application/json'
      },
    };
    
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/promotion/apply/`,
          { promotion_type: promotionType },
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  cancelPromotionAsync: () => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
        'Content-Type': 'application/json'
      },
    };
    
    return new Promise<any>(async (resolve, reject) => {
      axios
        .post(
          `${GlobalEnvironments.getApiUrl()}payment/promotion/cancel/`,
          {},
          config
        )
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // 검증 API (메뉴 링크 없이 URL 직접 접근용)
  verifyProjects: (projects: any[]) => {
    const key = util.getCookie(Cookies.authKey);
    var config = {
      headers: {
        Authorization: `Token ${key}`,
      },
    };
    return axios.post(
      `${GlobalEnvironments.getApiUrl()}verification/verify`,
      { projects },
      config
    );
  },
  getVerificationHistory: (page: number = 1, pageSize: number = 10) => {
    const key = util.getCookie(Cookies.authKey);
    return axios.get(
      `${GlobalEnvironments.getApiUrl()}verification/history?page=${page}&page_size=${pageSize}`,
      { headers: { Authorization: `Token ${key}` } }
    );
  },
  getVerificationHistoryDetail: (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    return axios.get(
      `${GlobalEnvironments.getApiUrl()}verification/history/${id}`,
      { headers: { Authorization: `Token ${key}` } }
    );
  },
  deleteVerificationHistory: (id: number) => {
    const key = util.getCookie(Cookies.authKey);
    return axios.delete(
      `${GlobalEnvironments.getApiUrl()}verification/history/${id}`,
      { headers: { Authorization: `Token ${key}` } }
    );
  },
  getVerificationDashboard: () => {
    const key = util.getCookie(Cookies.authKey);
    return axios.get(
      `${GlobalEnvironments.getApiUrl()}verification/dashboard`,
      { headers: { Authorization: `Token ${key}` } }
    );
  },
  getVerificationApplicationStatus: () => {
    const key = util.getCookie(Cookies.authKey);
    return axios.get(
      `${GlobalEnvironments.getApiUrl()}verification/application`,
      { headers: { Authorization: `Token ${key}` } }
    );
  },
  postVerificationApplication: (data: {
    name: string;
    phone: string;
    email: string;
    organization: string;
    department: string;
    content: string;
  }) => {
    const key = util.getCookie(Cookies.authKey);
    return axios.post(
      `${GlobalEnvironments.getApiUrl()}verification/application`,
      data,
      { headers: { Authorization: `Token ${key}` } }
    );
  },
};
